import React from "react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuSeparator,
} from "../../../components/ui/dropdown-menu";
import {
	FileIcon,
	Send,
	Share2,
	AlertTriangle,
	Printer,
	ListChecks,
	Copy,
	BellRing,
	PauseCircle,
	PlayCircle,
	History,
} from "lucide-react";
import { CancelOutlined } from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import type {
	InvoiceReminderActions,
	InvoiceWithPaymentsView,
	TableInvoiceRow,
	TablePaymentRow,
} from "../types/invoice-table.types";
import {
	getPdfTemplateType,
	toReminderInvoiceRef,
} from "../types/invoice-table.types";
import { ReminderInvoiceRef } from "../mahnwesen/payment-reminder.types";
import { TableFeatures } from "./abstract-invoice-table.component";
import { InvoiceType } from "../../../types/database-enums/invoices-enums";
import { useCentralStore } from "../../../store/Central";
import { getReminderActionLabel } from "../mahnwesen/utils/reminder-stage";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { MenuItemData, SharedMenuItem } from "./shared-menu-item.component";

const NON_DUNNABLE_TYPES: number[] = [
	InvoiceType.MAHNGEBUEHR,
	InvoiceType.RUECKVERGUETUNG,
	InvoiceType.PSEUDORG,
];

export interface InvoiceActionsMenuProps {
	invoice: TableInvoiceRow;
	onOpenFile?: (fileId: string) => void;
	onOpenFileInNewTab?: (
		pathName: string,
		invoiceType?: number | null,
		pdfTemplateType?: string | null
	) => void;
	onPrintAsCopy?: (invoice: TableInvoiceRow) => void;
	onOpenPaymentDialog?: (invoice: TableInvoiceRow) => void;
	onBookRemainderAsLoss?: (invoice: TableInvoiceRow) => void;
	onShareToggle?: (invoice: TableInvoiceRow) => void;
	onCancelInvoice?: (invoiceId: number) => void;
	onSendToZak?: (invoiceId: number) => void;
	onShowJobDocuments?: (invoice: TableInvoiceRow) => void;
	reminderActions?: InvoiceReminderActions;
	onClose?: () => void;
	isZakEnabled?: boolean;
	features?: TableFeatures;
}

// Shared logic hook
const useInvoiceActions = (invoice: InvoiceWithPaymentsView) => {
	const isCurrentlyShared = () => {
		return (
			invoice.shared_at &&
			(invoice.shared_until == null ||
				new Date(invoice.shared_until) > new Date())
		);
	};

	const isCanceledInvoice = () => {
		return !!invoice.invoice_canceled_at;
	};

	const isFullyPaid = () => {
		if (isCanceledInvoice()) return false; // Canceled invoices are never "paid"

		const payments = invoice.payments as unknown as TablePaymentRow[];
		const totalAllocated =
			payments && Array.isArray(payments)
				? payments
						.filter(
							(payment: TablePaymentRow) =>
								!payment.allocation_canceled_at
						)
						.reduce((sum: number, payment: TablePaymentRow) => {
							return sum + (payment.allocated_amount || 0);
						}, 0)
				: 0;
		return totalAllocated == (invoice.invoice_value || 0);
	};

	const hasRemainingAmount = () => {
		if (isCanceledInvoice()) return false; // Canceled invoices can't have remaining amounts

		const payments = invoice.payments as unknown as TablePaymentRow[];
		const totalAllocated =
			payments && Array.isArray(payments)
				? payments
						.filter(
							(payment: TablePaymentRow) =>
								!payment.allocation_canceled_at
						)
						.reduce((sum: number, payment: TablePaymentRow) => {
							return sum + (payment.allocated_amount || 0);
						}, 0)
				: 0;
		// Should show if there's any remaining amount (including full amount with no payments)
		return totalAllocated < (invoice.invoice_value || 0);
	};

	const getRemainingAmount = () => {
		if (isCanceledInvoice()) return 0;

		const payments = invoice.payments as unknown as TablePaymentRow[];
		const totalAllocated =
			payments && Array.isArray(payments)
				? payments
						.filter(
							(payment: TablePaymentRow) =>
								!payment.allocation_canceled_at
						)
						.reduce((sum: number, payment: TablePaymentRow) => {
							return sum + (payment.allocated_amount || 0);
						}, 0)
				: 0;
		return Math.max(0, (invoice.invoice_value || 0) - totalAllocated);
	};

	return {
		isCurrentlyShared,
		isFullyPaid,
		hasRemainingAmount,
		getRemainingAmount,
		isCanceledInvoice,
	};
};

const REMINDER_SHARE_ACTION: Record<string, string> = {
	shared: "nicht mehr teilen",
	unshared: "erneut teilen",
};

// Shared menu content component
export const SharedMenuContent: React.FC<{
	invoice: TableInvoiceRow;
	onOpenFile?: (fileId: string) => void;
	onOpenFileInNewTab?: (
		pathName: string,
		invoiceType?: number | null,
		pdfTemplateType?: string | null
	) => void;
	onPrintAsCopy?: (invoice: TableInvoiceRow) => void;
	onOpenPaymentDialog?: (invoice: TableInvoiceRow) => void;
	onBookRemainderAsLoss?: (invoice: TableInvoiceRow) => void;
	onShareToggle?: (invoice: TableInvoiceRow) => void;
	onCancelInvoice?: (invoiceId: number) => void;
	onSendToZak?: (invoiceId: number) => void;
	onShowJobDocuments?: (invoice: TableInvoiceRow) => void;
	reminderActions?: InvoiceReminderActions;
	onClose?: () => void;
	isZakEnabled?: boolean;
	asDropdown?: boolean;
	features?: TableFeatures;
}> = ({
	invoice,
	onOpenFile,
	onOpenFileInNewTab,
	onPrintAsCopy,
	onOpenPaymentDialog,
	onBookRemainderAsLoss,
	onShareToggle,
	onCancelInvoice,
	onSendToZak,
	onShowJobDocuments,
	reminderActions,
	onClose,
	isZakEnabled = false,
	asDropdown = true,
	features,
}) => {
	const maxReminderStage = useCentralStore(
		(state) => state.organization?.mahnung_max_count ?? 0
	);
	const {
		isCurrentlyShared,
		isFullyPaid,
		hasRemainingAmount,
		getRemainingAmount,
		isCanceledInvoice,
	} = useInvoiceActions(invoice);

	const isFeeInvoice = invoice.invoice_type === InvoiceType.MAHNGEBUEHR;
	const reminderStage = invoice.reminder_stage ?? 0;
	const isDunnable =
		!!reminderActions &&
		!features?.historicalView &&
		hasRemainingAmount() &&
		!NON_DUNNABLE_TYPES.includes(invoice.invoice_type ?? -1);

	const reminderRef = toReminderInvoiceRef(invoice);
	const hasReminderHistory = reminderStage > 0 || !!invoice.reminder_paused;
	const runReminderAction = (
		action?: (invoice: ReminderInvoiceRef) => void
	) => {
		if (reminderRef) action?.(reminderRef);
	};

	const reminderMenuItems: MenuItemData[] = [
		{
			icon: <BellRing className="w-4 h-4 flex-shrink-0" />,
			label: getReminderActionLabel(reminderStage + 1),
			onClick: () => runReminderAction(reminderActions?.onCreateReminder),
			disabled:
				!!invoice.reminder_paused || reminderStage >= maxReminderStage,
			show: isDunnable,
			testId: "invoice-menu-create-reminder",
		},
		{
			icon: <PauseCircle className="w-4 h-4 flex-shrink-0" />,
			label: "Mahnstopp setzen...",
			onClick: () => runReminderAction(reminderActions?.onPauseReminders),
			show: isDunnable && !invoice.reminder_paused,
			testId: "invoice-menu-pause-reminders",
		},
		{
			icon: <PlayCircle className="w-4 h-4 flex-shrink-0" />,
			label: "Mahnstopp aufheben",
			onClick: () =>
				runReminderAction(reminderActions?.onResumeReminders),
			show: !!reminderActions && !!invoice.reminder_paused,
			testId: "invoice-menu-resume-reminders",
		},
		{
			icon: <Share2 className="w-4 h-4 flex-shrink-0" />,
			label: `${getReminderStageLabel(reminderStage)} ${
				REMINDER_SHARE_ACTION[invoice.reminder_share_state ?? ""] ??
				"teilen"
			}`,
			onClick: () =>
				runReminderAction(reminderActions?.onToggleReminderShare),
			show: !!reminderActions && reminderStage > 0 && !isFeeInvoice,
			testId: "invoice-menu-toggle-reminder-share",
		},
		{
			icon: <History className="w-4 h-4 flex-shrink-0" />,
			label: "Mahnverlauf anzeigen",
			onClick: () =>
				runReminderAction(reminderActions?.onShowReminderHistory),
			disabled: !hasReminderHistory,
			show: !!reminderActions && !isFeeInvoice,
			testId: "invoice-menu-reminder-history",
		},
	];

	const upperMenuItems: MenuItemData[] = [
		{
			icon: <FileIcon className="w-4 h-4 flex-shrink-0" />,
			label: "Rechnungsdatei öffnen",
			onClick: () => {
				if (invoice.file_id && onOpenFile) {
					onOpenFile(invoice.file_id);
				}
			},
			disabled: !invoice.file_id,
			show: !!onOpenFile,
		},
		{
			icon: <Printer className="w-4 h-4 flex-shrink-0" />,
			label: "Rechnungsdatei drucken",
			onClick: () => {
				if (invoice.path_name && onOpenFileInNewTab) {
					onOpenFileInNewTab(
						invoice.path_name,
						invoice.invoice_type,
						getPdfTemplateType(invoice)
					);
				}
			},
			disabled: !invoice.path_name,
			show: !!onOpenFileInNewTab,
		},
		{
			icon: <Copy className="w-4 h-4 flex-shrink-0" />,
			label: "Als Kopie drucken",
			onClick: () => onPrintAsCopy?.(invoice),
			disabled: !invoice.path_name,
			show: !!onPrintAsCopy,
		},
		{
			icon: <Share2 className="w-4 h-4 flex-shrink-0" />,
			label: isCurrentlyShared() ? "Teilen aufheben" : "Rechnung teilen",
			onClick: () => onShareToggle?.(invoice),
			disabled: !invoice.invoice_id || !invoice.client_id,
			show: !!onShareToggle && !isFeeInvoice,
		},
		{
			icon: <ListChecks className="w-4 h-4 flex-shrink-0" />,
			label: "Auftragsdokumente anzeigen",
			onClick: () => onShowJobDocuments?.(invoice),
			disabled: !invoice.invoice_id,
			show: !!onShowJobDocuments && !isFeeInvoice,
		},
		/* {
			icon: <Send className="w-4 h-4 flex-shrink-0" />,
			label: "An ZAK senden",
			onClick: () => {
				if (window.confirm('Möchten Sie diese Rechnung an ZAK senden?')) {
					onSendToZak?.(invoice.invoice_id!);
				}
			},
			show: isZakEnabled && !invoice.is_sent_to_zak && !!onSendToZak,
		}, */
	];

	const lowerMenuItems: MenuItemData[] = [
		{
			icon: (
				<ReceiptIcon
					style={{ fontSize: "16px" }}
					className="flex-shrink-0"
				/>
			),
			label: "Zahlung erfassen",
			onClick: () => onOpenPaymentDialog?.(invoice),
			disabled: false,
			show:
				!isCanceledInvoice() &&
				!!onOpenPaymentDialog &&
				!features?.historicalView,
		},
		{
			icon: <AlertTriangle className="w-4 h-4 flex-shrink-0" />,
			label: (() => {
				const totalAllocated =
					(invoice.payments as unknown as TablePaymentRow[])
						?.filter((payment) => !payment.allocation_canceled_at)
						.reduce((sum, payment) => {
							return sum + (payment.allocated_amount || 0);
						}, 0) || 0;
				return totalAllocated > 0
					? "Restbetrag als Verlust buchen"
					: "Als Verlust buchen";
			})(),
			onClick: () => {
				onBookRemainderAsLoss?.(invoice);
			},
			className: "text-red-600 focus:text-red-600",
			show:
				!isCanceledInvoice() &&
				hasRemainingAmount() &&
				!!onBookRemainderAsLoss &&
				features?.allowBookAsLoss &&
				!features?.historicalView,
		},
		{
			icon: (
				<CancelOutlined
					style={{ fontSize: "16px" }}
					className="flex-shrink-0"
				/>
			),
			label: "Rechnung stornieren...",
			onClick: () => {
				if (invoice.invoice_id) {
					onCancelInvoice?.(invoice.invoice_id);
				}
			},
			className: "text-red-600 focus:text-red-600",
			show:
				!isCanceledInvoice() &&
				!!onCancelInvoice &&
				!features?.historicalView,
		},
	];

	return (
		<>
			{upperMenuItems.map((item, index) => (
				<SharedMenuItem
					key={`upper-${index}`}
					item={item}
					asDropdown={asDropdown}
					onClose={onClose}
				/>
			))}

			{reminderMenuItems.some((item) => item.show) &&
				(asDropdown ? (
					<DropdownMenuSeparator />
				) : (
					<div className="h-px bg-border my-1" />
				))}

			{reminderMenuItems.map((item, index) => (
				<SharedMenuItem
					key={`reminder-${index}`}
					item={item}
					asDropdown={asDropdown}
					onClose={onClose}
				/>
			))}

			{lowerMenuItems.filter((item) => item.show == true).length > 0 &&
				(asDropdown ? (
					<DropdownMenuSeparator />
				) : (
					<div className="h-px bg-border my-1" />
				))}

			{lowerMenuItems.map((item, index) => (
				<SharedMenuItem
					key={`lower-${index}`}
					item={item}
					asDropdown={asDropdown}
					onClose={onClose}
				/>
			))}
		</>
	);
};

// Version for context menus (uses regular buttons)
export const InvoiceActionsButtonContent: React.FC<InvoiceActionsMenuProps> = (
	props
) => {
	return <SharedMenuContent {...props} asDropdown={false} />;
};

export interface InvoiceContextMenuProps {
	x: number;
	y: number;
	visible: boolean;
	invoice: TableInvoiceRow;
	onOpenFile?: (fileId: string) => void;
	onOpenFileInNewTab?: (
		pathName: string,
		invoiceType?: number | null,
		pdfTemplateType?: string | null
	) => void;
	onPrintAsCopy?: (invoice: TableInvoiceRow) => void;
	onOpenPaymentDialog?: (invoice: TableInvoiceRow) => void;
	onBookRemainderAsLoss?: (invoice: TableInvoiceRow) => void;
	onShareToggle?: (invoice: TableInvoiceRow) => void;
	onCancelInvoice?: (invoiceId: number) => void;
	onSendToZak?: (invoiceId: number) => void;
	onShowJobDocuments?: (invoice: TableInvoiceRow) => void;
	reminderActions?: InvoiceReminderActions;
	onClose?: () => void;
	isZakEnabled?: boolean;
	features?: TableFeatures;
}

export const InvoiceContextMenu: React.FC<InvoiceContextMenuProps> = ({
	x,
	y,
	visible,
	invoice,
	onOpenFile,
	onOpenFileInNewTab,
	onPrintAsCopy,
	onOpenPaymentDialog,
	onBookRemainderAsLoss,
	onShareToggle,
	onCancelInvoice,
	onSendToZak,
	onShowJobDocuments,
	reminderActions,
	onClose,
	isZakEnabled = false,
	features,
}) => {
	if (!visible) return null;

	// Adjust position to prevent menu from going off-screen
	const menuWidth = 200; // Approximate menu width
	const menuHeight = 300; // Approximate menu height
	const adjustedX = x + menuWidth > window.innerWidth ? x - menuWidth : x;
	const adjustedY = y + menuHeight > window.innerHeight ? y - menuHeight : y;

	return (
		<div
			className="fixed z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
			style={{
				left: Math.max(0, adjustedX),
				top: Math.max(0, adjustedY),
			}}
			onClick={(e) => e.stopPropagation()}
		>
			<div className="p-1">
				<InvoiceActionsButtonContent
					invoice={invoice}
					onOpenFile={onOpenFile}
					onOpenFileInNewTab={onOpenFileInNewTab}
					onPrintAsCopy={onPrintAsCopy}
					onOpenPaymentDialog={onOpenPaymentDialog}
					onBookRemainderAsLoss={onBookRemainderAsLoss}
					onShareToggle={onShareToggle}
					onCancelInvoice={onCancelInvoice}
					onSendToZak={onSendToZak}
					onShowJobDocuments={onShowJobDocuments}
					reminderActions={reminderActions}
					onClose={onClose}
					isZakEnabled={isZakEnabled}
					features={features}
				/>
			</div>
		</div>
	);
};
